import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { LOGO_URL } from "@/components/Navbar";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", address: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      setDone(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Kunne ikke oprette konto");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6 grain-bg">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="register-success">
          <span className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-blue-50 text-blue-600">
            <CheckCircle2 className="h-7 w-7" />
          </span>
          <h1 className="mt-5 text-2xl font-display font-black tracking-tight text-slate-900">Konto oprettet!</h1>
          <p className="mt-2 text-slate-600">
            Din adgang skal godkendes af J-Computer. Du får besked, når du kan logge ind.
            Vil du hurtigere i gang, kan du lave en <Link to="/booking" className="text-blue-600 font-medium">booking</Link> —
            så bliver din adgang godkendt automatisk.
          </p>
          <Button onClick={() => navigate("/login")} className="mt-6 w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700" data-testid="go-login">
            Til login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 grain-bg">
        <Link to="/" className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="J-Computer logo" className="h-10 w-10 rounded-lg object-cover ring-1 ring-slate-700" />
          <span className="text-xl font-black tracking-tighter font-display">J-Computer<span className="text-blue-500">.</span></span>
        </Link>
        <div>
          <h2 className="text-4xl font-display font-black tracking-tighter leading-tight">Bliv kunde</h2>
          <p className="mt-4 text-slate-400 max-w-sm">
            Opret en konto og få din egen portal med bookinger, produkter og direkte beskeder til mig.
          </p>
        </div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} J-Computer</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-display font-black tracking-tight text-slate-900">Opret konto</h1>
          <p className="mt-1 text-sm text-slate-500">Adgang godkendes af J-Computer.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Navn</Label>
              <Input id="name" required value={form.name} onChange={set("name")} data-testid="reg-name"
                className="h-11 bg-slate-50 focus-visible:ring-blue-600" placeholder="Dit fulde navn" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="reg-email"
                className="h-11 bg-slate-50 focus-visible:ring-blue-600" placeholder="din@email.dk" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="phone">Telefon</Label>
                <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="reg-phone"
                  className="h-11 bg-slate-50 focus-visible:ring-blue-600" placeholder="12 34 56 78" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Adgangskode</Label>
                <Input id="password" type="password" required value={form.password} onChange={set("password")} data-testid="reg-password"
                  className="h-11 bg-slate-50 focus-visible:ring-blue-600" placeholder="min. 6 tegn" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="address">Adresse (valgfri)</Label>
              <Input id="address" value={form.address} onChange={set("address")} data-testid="reg-address"
                className="h-11 bg-slate-50 focus-visible:ring-blue-600" placeholder="Vej, by, postnr." />
            </div>
            <Button type="submit" disabled={loading} data-testid="reg-submit"
              className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700">
              {loading ? "Opretter…" : "Opret konto"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600 text-center">
            Har du allerede en konto?{" "}
            <Link to="/login" className="text-blue-600 font-medium hover:underline" data-testid="to-login">Log ind</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
